import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { formatCurrency } from '../../utils/payrollCalculations';

interface PayrollPeriod {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed' | 'archived';
  isLocked: boolean;
  isVisibleToEmployees: boolean;
  lockedAt: string | null;
  approvedAt: string | null;
  totalGrossSalary: number;
  totalDeductions: number;
  totalNetSalary: number;
  totalNapsa: number;
  totalPaye: number;
  createdAt: string;
}

const PayrollPeriods: React.FC = () => {
  const buildDateString = (year: string, month: string, day: number) => {
    return `${year}-${(parseInt(month, 10) + 1).toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
  };

  const getMonthStartDate = (year: string, month: string) => buildDateString(year, month, 1);

  const getMonthEndDate = (year: string, month: string) =>
    buildDateString(year, month, new Date(parseInt(year, 10), parseInt(month, 10) + 1, 0).getDate());

  const syncDateParts = (value: string) => {
    if (!value) {
      return null;
    }

    const [year, month] = value.split('-');
    if (!year || !month) {
      return null;
    }

    return {
      year,
      month: (parseInt(month, 10) - 1).toString(),
    };
  };

  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<PayrollPeriod | null>(null);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false);
// Form state - Initialize with current year and month
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // -11

  const createEmptyFormData = () => ({
    name: '',
    startDate: '',
    endDate: '',
    startYear: currentYear.toString(),
    endYear: currentYear.toString(),
    startMonth: currentMonth.toString(),
    endMonth: currentMonth.toString(),
  });

  const [formData, setFormData] = useState(createEmptyFormData);
// Generate a wider year range so past and future payroll periods can be created easily
  const years = Array.from({ length: 41 }, (_, i) => (currentYear - 20 + i).toString());
// Month names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
// Get days in a month
  const getDaysInMonth = (year: string, month: string) => {
    const y = parseInt(year);
    const m = parseInt(month);
    return new Date(y, m + 1, 0).getDate();
  };
// Set default dates when year or month changes
  const handleYearChange = (field: 'startYear' | 'endYear', year: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: year };
// Update the date with the new year
      if (field === 'startYear') {
        const day = prev.startDate ? parseInt(prev.startDate.split('-')[2]) : 1;
        const month = parseInt(prev.startMonth);
        const maxDay = getDaysInMonth(year, prev.startMonth);
        newData.startDate = buildDateString(year, prev.startMonth, Math.min(day, maxDay));
      }
      if (field === 'endYear') {
        const day = prev.endDate ? parseInt(prev.endDate.split('-')[2]) : getDaysInMonth(year, prev.endMonth);
        const maxDay = getDaysInMonth(year, prev.endMonth);
        newData.endDate = buildDateString(year, prev.endMonth, Math.min(day, maxDay));
      }
      return newData;
    });
  };

  const handleMonthChange = (field: 'startMonth' | 'endMonth', month: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: month };
      const year = field === 'startMonth' ? prev.startYear : prev.endYear;
      const m = parseInt(month);
      const day = field === 'startMonth' 
        ? (prev.startDate ? parseInt(prev.startDate.split('-')[2]) : 1)
        : (prev.endDate ? parseInt(prev.endDate.split('-')[2]) : getDaysInMonth(prev.endYear, month));
      const maxDay = getDaysInMonth(year, month);
      const finalDay = Math.min(day, maxDay);
      const dateStr = buildDateString(year, month, finalDay);
      
      if (field === 'startMonth') {
        newData.startDate = dateStr;
      } else {
        newData.endDate = dateStr;
      }
      return newData;
    });
  };

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(getApiUrl('payroll/periods'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeriods(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching periods');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (new Date(formData.endDate).getTime() < new Date(formData.startDate).getTime()) {
        setError('End date cannot be before start date.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token') || '';
      await axios.post(getApiUrl('payroll/periods'), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Payroll period created successfully');
      setShowModal(false);
      setFormData(createEmptyFormData());
      fetchPeriods();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating period');
    } finally {
      setLoading(false);
    }
  };

  const handleLockPeriod = async () => {
    if (!selectedPeriod) return;

    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/periods/${selectedPeriod.id}/lock`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(selectedPeriod.isLocked ? 'Period unlocked successfully' : 'Period locked successfully');
      setShowLockConfirm(false);
      setSelectedPeriod(null);
      fetchPeriods();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating period');
    }
  };

  const handleToggleVisibility = async () => {
    if (!selectedPeriod) return;

    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/periods/${selectedPeriod.id}/visibility`), {
        visible: !selectedPeriod.isVisibleToEmployees
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Payslips ${selectedPeriod.isVisibleToEmployees ? 'hidden from' : 'visible to'} employees`);
      setShowVisibilityConfirm(false);
      setSelectedPeriod(null);
      fetchPeriods();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating visibility');
    }
  };

  const handleApprove = async (period: PayrollPeriod) => {
    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/periods/${period.id}/approve`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Period approved successfully');
      fetchPeriods();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error approving period');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string, isLocked: boolean) => {
    if (isLocked) {
      return 'bg-gray-100 text-gray-600 border border-gray-300';
    }
    const styles: Record<string, string> = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-red-100 text-red-800',
      archived: 'bg-gray-100 text-gray-600',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && periods.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payroll Periods</h1>
        <button
          onClick={() => {
            setFormData(createEmptyFormData());
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          + Add Period
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-sm text-gray-600">Total Periods</p>
          <p className="text-2xl font-bold text-blue-600">{periods.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-sm text-gray-600">Open Periods</p>
          <p className="text-2xl font-bold text-green-600">
            {periods.filter(p => p.status === 'open' && !p.isLocked).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-sm text-gray-600">Locked Periods</p>
          <p className="text-2xl font-bold text-gray-600">
            {periods.filter(p => p.isLocked).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-sm text-gray-600">Visible to Staff</p>
          <p className="text-2xl font-bold text-purple-600">
            {periods.filter(p => p.isVisibleToEmployees).length}
          </p>
        </div>
      </div>

      {/* Periods List */}
      <div className="bg-white rounded-lg shadow border">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Start Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">End Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Visibility</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Total Net</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {periods.map(period => (
                <tr key={period.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{period.name}</p>
                    <p className="text-sm text-gray-500">
                      Created: {formatDate(period.createdAt)}
                    </p>
                  </td>
                  <td className="px-4 py-3">{formatDate(period.startDate)}</td>
                  <td className="px-4 py-3">{formatDate(period.endDate)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-sm ${getStatusBadge(period.status, period.isLocked)}`}>
                      {period.isLocked ? 'Locked' : period.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {period.isVisibleToEmployees ? (
                      <span className="flex items-center text-green-600">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Visible
                      </span>
                    ) : (
                      <span className="flex items-center text-gray-400">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v3a1 1 0 11-2 0V7zm0 6a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
                        </svg>
                        Hidden
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {formatCurrency(Number(period.totalNetSalary || 0))}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {!period.isLocked && period.status === 'open' && (
                        <button
                          onClick={() => handleApprove(period)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          title="Approve period"
                        >
                          Approve
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedPeriod(period);
                          setShowVisibilityConfirm(true);
                        }}
                        className={`text-sm ${period.isVisibleToEmployees ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'}`}
                        title={period.isVisibleToEmployees ? 'Hide from employees' : 'Show to employees'}
                      >
                        {period.isVisibleToEmployees ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPeriod(period);
                          setShowLockConfirm(true);
                        }}
                        className={`text-sm ${period.isLocked ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                        title={period.isLocked ? 'Unlock period' : 'Lock period'}
                      >
                        {period.isLocked ? 'Unlock' : 'Lock'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {periods.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No payroll periods found. Create a period to get started.
          </div>
        )}
      </div>

      {/* Information Card */}
      <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-4">
        <h4 className="font-semibold text-blue-800 mb-2">Payroll Period Guidelines</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Open</strong>: Period is being processed, payslips can be edited</li>
          <li>• <strong>Approved</strong>: Period approved, ready for payment processing</li>
          <li>• <strong>Locked</strong>: Period is finalized, no changes allowed (recommended before payment)</li>
          <li>• <strong>Visibility</strong>: Controls when employees can see their payslips</li>
          <li>• <strong>Locking</strong>: Once locked, only admins can unlock for corrections</li>
        </ul>
      </div>

      {/* Add Period Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Payroll Period</h2>
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Create payroll periods for any month or date range. You can backdate a past period or prepare a future one, and it will be available in bulk payslip generation.
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Period Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., January 2025, Dec 2025 Bonus, Term 1 2026"
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Start Year *</label>
                <select
                  value={formData.startYear}
                  onChange={(e) => handleYearChange('startYear', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Start Month *</label>
                <select
                  value={formData.startMonth}
                  onChange={(e) => handleMonthChange('startMonth', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index.toString()}>{month}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => {
                    const nextDate = e.target.value;
                    const parts = syncDateParts(nextDate);
                    setFormData({
                      ...formData,
                      startDate: nextDate,
                      startYear: parts?.year ?? formData.startYear,
                      startMonth: parts?.month ?? formData.startMonth,
                    });
                  }}
                  className="w-full border rounded px-3 py-2"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Choose any valid past or future start date. This field starts blank intentionally.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">End Year *</label>
                <select
                  value={formData.endYear}
                  onChange={(e) => handleYearChange('endYear', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">End Month *</label>
                <select
                  value={formData.endMonth}
                  onChange={(e) => handleMonthChange('endMonth', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index.toString()}>{month}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    const nextDate = e.target.value;
                    const parts = syncDateParts(nextDate);
                    setFormData({
                      ...formData,
                      endDate: nextDate,
                      endYear: parts?.year ?? formData.endYear,
                      endMonth: parts?.month ?? formData.endMonth,
                    });
                  }}
                  className="w-full border rounded px-3 py-2"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Choose any valid past or future end date. This field starts blank intentionally, and it must be on or after the start date.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Period'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lock Confirmation Modal */}
      {showLockConfirm && selectedPeriod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedPeriod.isLocked ? 'Unlock Period' : 'Lock Period'}
            </h2>
            <p className="text-gray-600 mb-4">
              {selectedPeriod.isLocked 
                ? 'Are you sure you want to unlock this period? This will allow modifications to payslips.'
                : 'Are you sure you want to lock this period? This will prevent any further modifications. You will need admin access to unlock it later.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowLockConfirm(false);
                  setSelectedPeriod(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleLockPeriod}
                className={`px-4 py-2 rounded text-white ${selectedPeriod.isLocked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {selectedPeriod.isLocked ? 'Unlock' : 'Lock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visibility Confirmation Modal */}
      {showVisibilityConfirm && selectedPeriod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedPeriod.isVisibleToEmployees ? 'Hide Payslips' : 'Show Payslips'}
            </h2>
            <p className="text-gray-600 mb-4">
              {selectedPeriod.isVisibleToEmployees 
                ? 'Are you sure you want to hide payslips from employees? They will no longer be able to view or download them.'
                : 'Are you sure you want to show payslips to employees? They will be able to view and download their payslips.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowVisibilityConfirm(false);
                  setSelectedPeriod(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleVisibility}
                className={`px-4 py-2 rounded text-white ${selectedPeriod.isVisibleToEmployees ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {selectedPeriod.isVisibleToEmployees ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPeriods;









